import baseAxios, { AxiosResponse } from 'axios';
import {
  Businesses,
  BusinessSettings,
  BusinessTaxRates,
  Services,
  ShopOnboardingID,
} from '@prisma/client';
import type Stripe from 'stripe';
import API_URL from '../apiconfig';
import {
  Appointment,
  Business,
  Employee,
  EmployeeToBusiness,
  FilterOption,
  LoadedUser,
  Service,
  Trip,
} from '../interfaces';
import {
  getArtistInitials,
  getArtistName,
  getInitials,
  isUserAdminOrManager,
} from '../utils/global/helper';
import axios from './api';
import { getApiErrorMessage } from './apiError';
import {
  AddBusinessTripPayload,
  BusinessService,
  BusinessTaxRatesPayload,
  ChiliPiperResponse,
  CompleteOnboardingStepResponse,
  OnboardingResponse,
  ServiceIntakeQuestion,
  SkipOnboardingStepResponse,
  TerminalProducts,
  UpdateBusinessPayload,
  UpdateBusinessSettingsPayload,
  UpdatePaymentsRatesPayload,
  UpsertBusinessServiceDto,
} from '../types/businesses';
import { Resource } from '../types/calendar';
import {
  GetStripeAccountResponse,
  InitiatePayoutResponse,
  LinkStripeAccountDto,
  LinkStripeAccountResponse,
  StripeSummary,
} from '../types/stripe';
import client from '../utils/react-query/client';
import { ResourceBlock } from '../types/employees';
import { PaymentsCapabilities } from '../types/payments';
import {
  UNASSIGNED_RESOURCE_COLOR,
  UNASSIGNED_RESOURCE_ID,
} from '../constants/calendar';

export async function getBusiness(businessId: string) {
  try {
    const business = await axios.get(`${API_URL}/v1/businesses/${businessId}`);

    return business.data.result;
  } catch (error) {
    throw new Error('Cannot get business', { cause: error });
  }
}

export async function getTerminalReader(businessId: string) {
  try {
    const reader = await axios.get(
      `${API_URL}/v1/businesses/${businessId}/terminal-reader`,
    );

    return reader.data.result as { reader: Stripe.Terminal.Reader };
  } catch (error) {
    throw new Error('Cannot get business', { cause: error });
  }
}

export async function getTerminalProducts() {
  try {
    const response = await axios.get(
      `${API_URL}/v1/businesses/terminal-reader/products`,
    );

    return response.data.result as TerminalProducts;
  } catch (error) {
    throw new Error('Cannot get terminal products', { cause: error });
  }
}

export async function initatePurchaseTerminalCheckout({
  priceId,
  businessId,
  successUrl,
}: {
  priceId: string;
  businessId: string;
  successUrl: string;
}) {
  try {
    const response = await axios.post(
      `${API_URL}/v1/businesses/${businessId}/terminal-reader/checkout`,
      {
        priceId,
        successUrl,
      },
    );

    return response.data.result as Stripe.Checkout.Session;
  } catch (error) {
    throw new Error('Cannot initiate purchase terminal checkout', {
      cause: error,
    });
  }
}

export async function cancelCurrentTerminalReaderAction(
  businessId: string,
  readerId: string,
): Promise<AxiosResponse> {
  const response = await axios
    .patch(`${API_URL}/v1/businesses/${businessId}/cancel-terminal-checkout`, {
      readerId,
    })
    .catch(error => {
      throw new Error(
        getApiErrorMessage(error, 'Cannot cancel terminal checkout'),
        { cause: error },
      );
    });
  return response;
}

export const getEmployeeToBusiness = (
  loadedUser: LoadedUser,
  employeeId: string,
) => loadedUser.business.employees.find(etb => etb.employeeId === employeeId);

export const generateColorForArtist = (
  artistId: string,
  loadedUser: LoadedUser,
): string => {
  const employeeToBusiness = getEmployeeToBusiness(loadedUser, artistId);
  if (employeeToBusiness?.color) {
    return employeeToBusiness.color;
  }

  const vibrantHues = [0, 15, 30, 45, 120, 150, 195, 220, 260, 280, 330];
  const artistHash = Array.from(artistId).reduce(
    (acc, char) => acc + char.charCodeAt(0),
    0,
  );

  const hue = vibrantHues[artistHash % vibrantHues.length];

  // Slightly lower maximum saturation for a bit less brightness
  const saturation = 70 + (artistHash % 21); // Values between 70% and 90%

  // A bit higher minimum lightness to reduce brightness slightly
  const lightness = 45 + (artistHash % 16); // Values between 45% and 60%

  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

export function getArtistOption(etb: EmployeeToBusiness, user: LoadedUser) {
  return {
    label: getArtistName(etb),
    value: etb.employeeId,
    color: generateColorForArtist(etb.employeeId, user),
  };
}

export function getArtistOptions(
  user: LoadedUser,
  includeFirstAvailable?: boolean,
) {
  const { business } = user;

  const artistOptions: FilterOption[] = [];
  if (business.employees) {
    business.employees.forEach(employee => {
      if (
        employee.isBookable ||
        user.business.services?.find(service =>
          service.employeesToServices.find(
            employeeToService =>
              employeeToService.employeeId === employee.employeeId,
          ),
        )
      ) {
        artistOptions.push(getArtistOption(employee, user));
      }
    });
  }

  if (includeFirstAvailable) {
    artistOptions.unshift({
      label: 'First Available',
      value: 'first-available',
      color: '#000000',
    });
  }

  return artistOptions;
}

export function getArtistsForService(user: LoadedUser, service: Service) {
  const { business } = user;

  const artistOptions: FilterOption[] = [];
  if (business.employees) {
    business.employees.forEach(employee => {
      if (
        service.employeesToServices.find(
          mapping => mapping.employeeId === employee.employeeId,
        )
      ) {
        artistOptions.push({
          label: getArtistName(employee),
          value: employee.employeeId,
          color: generateColorForArtist(employee.employeeId, user),
        });
      }
    });
  }
  return artistOptions;
}

export function getArtistOptionsForService({
  serviceId,
  user,
  addQueueOption,
  addSelfIfEmpty,
}: {
  serviceId: string | undefined;
  user: LoadedUser;
  addQueueOption?: boolean;
  addSelfIfEmpty?: boolean;
}) {
  let tempArtistOptions: FilterOption[] = [];
  const tempService = user?.business.services?.find(
    s => String(s.serviceId) === String(serviceId),
  );

  if (!serviceId || serviceId === '0') {
    tempArtistOptions = getArtistOptions(user);
  } else if (tempService) {
    tempArtistOptions = getArtistsForService(user, tempService);
  }

  if (!isUserAdminOrManager(user)) {
    tempArtistOptions = tempArtistOptions.filter(
      artist => artist.value === user?.userId,
    );
  }

  if (addQueueOption) {
    tempArtistOptions.push({
      label: 'Add to walk-in queue',
      value: '',
      color: '#000000',
    });
  }

  if (addSelfIfEmpty && !tempArtistOptions.length) {
    const selfEmployeeToBusiness = getEmployeeToBusiness(user, user.userId);
    if (selfEmployeeToBusiness) {
      tempArtistOptions.push(getArtistOption(selfEmployeeToBusiness, user));
    }
  }

  return tempArtistOptions;
}

export function getResourceMap(user: LoadedUser) {
  const { business } = user;

  const resourceMap: Resource[] = [];
  if (business.employees) {
    business.employees.forEach(employee => {
      if (
        employee.isBookable ||
        user.business.services?.find(service =>
          service.employeesToServices.find(
            employeeToService =>
              employeeToService.employeeId === employee.employeeId,
          ),
        )
      ) {
        resourceMap.push({
          resourceId: employee.employeeId,
          resourceTitle: employee.displayName || getArtistInitials(employee),
          color: generateColorForArtist(employee.employeeId, user),
          profileImage: employee.profileImage,
          initials: getArtistInitials(employee),
        });
      }
    });
  }
  return resourceMap;
}

export const getEmployeesResourcesList = (
  user: LoadedUser,
  hasUnassignedAppointment = false,
  appointments: Appointment[] = [],
): Resource[] => {
  if (user.role === 'EMPLOYEE') {
    return [
      {
        resourceId: user.userId,
        resourceTitle: user.employee.displayName || '',
        color: generateColorForArtist(user.userId, user),
        profileImage: user.employee.profileImage,
        initials: getInitials({
          firstName: user.firstName,
          lastName: user.lastName,
        }),
      },
    ];
  }

  const resources = getResourceMap(user);

  if (isUserAdminOrManager(user) && hasUnassignedAppointment) {
    resources.push({
      resourceId: UNASSIGNED_RESOURCE_ID,
      resourceTitle: 'Unassigned',
      color: UNASSIGNED_RESOURCE_COLOR,
    });
  }

  if (isUserAdminOrManager(user) && appointments.length > 0) {
    const activeIds = new Set(resources.map(r => r.resourceId));
    const inactiveSeen = new Set<string>();
    appointments.forEach(appointment => {
      const id = appointment.employeeId;
      if (!id || activeIds.has(id) || inactiveSeen.has(id)) return;
      inactiveSeen.add(id);
      const name =
        appointment.employee?.firstName || appointment.employee?.lastName
          ? `${appointment.employee?.firstName ?? ''} ${
              appointment.employee?.lastName ?? ''
            }`.trim()
          : 'Former employee';
      resources.push({
        resourceId: id,
        resourceTitle: `${name} (Inactive)`,
        color: generateColorForArtist(id, user),
        isInactive: true,
        initials: getInitials({
          firstName: appointment.employee?.firstName,
          lastName: appointment.employee?.lastName,
        }),
      });
    });
  }

  return resources;
};

export async function removeEmployeeFromShop(
  employee: Employee,
  businessId: string,
) {
  try {
    const result = await axios.delete(
      `${API_URL}/v1/businesses/${businessId}/${employee.employeeId}`,
    );

    return result;
  } catch (error) {
    throw new Error('Cannot remove employee', { cause: error });
  }
}

export async function updateBusiness(
  businessId: string,
  payload: UpdateBusinessPayload,
) {
  const response = await axios.patch(
    `${API_URL}/v1/businesses/${businessId}`,
    payload,
  );

  return response?.data?.result as Business;
}

export async function updateBusinessTaxRates(
  businessId: string,
  payload: BusinessTaxRatesPayload,
) {
  try {
    const response = await axios.patch(
      `${API_URL}/v1/businesses/${businessId}/tax-rates`,
      payload,
    );

    return response?.data?.result as BusinessTaxRates;
  } catch (error) {
    throw new Error('Cannot update business', { cause: error });
  }
}

export async function addBusinessTrip(
  businessId: string,
  payload: AddBusinessTripPayload,
) {
  try {
    const response = await axios.post(`${API_URL}/v1/trips/business-trip`, {
      ...payload,
      businessId,
    });

    return response?.data?.result as Trip;
  } catch (error) {
    throw new Error('Cannot add trip', { cause: error });
  }
}

export async function getBusinessSettings(businessId: string) {
  try {
    const response = await axios.get(
      `${API_URL}/v1/businesses/${businessId}/settings`,
    );

    return response?.data?.result as BusinessSettings;
  } catch (error) {
    throw new Error('Cannot get business settings', { cause: error });
  }
}

export async function updateBusinessSettings(
  businessId: string,
  payload: Omit<UpdateBusinessSettingsPayload, 'bookInAdvanceHours'> & {
    bookInAdvanceMinutes: number;
  },
) {
  try {
    const response = await axios.patch(
      `${API_URL}/v1/businesses/${businessId}/settings`,
      payload,
    );

    return response?.data?.result as BusinessSettings;
  } catch (error) {
    throw new Error('Cannot update business settings', { cause: error });
  }
}

export async function getBusinessServices(businessId: string) {
  try {
    const response = await axios.get(
      `${API_URL}/v1/businesses/${businessId}/services`,
    );

    return response?.data?.result as BusinessService[];
  } catch (error) {
    throw new Error('Cannot get business services', { cause: error });
  }
}

export async function createBusinessService(
  businessId: string,
  body: UpsertBusinessServiceDto,
) {
  try {
    const response = await axios.post(
      `${API_URL}/v1/businesses/${businessId}/services`,
      body,
    );

    return response?.data?.result as BusinessService;
  } catch (error) {
    throw new Error('Cannot create business service', { cause: error });
  }
}

export async function updateBusinessService(
  businessId: string,
  serviceId: number,
  body: UpsertBusinessServiceDto,
) {
  try {
    const response = await axios.patch(
      `${API_URL}/v1/businesses/${businessId}/services/${serviceId}`,
      body,
    );

    return response?.data?.result as BusinessService;
  } catch (error) {
    throw new Error('Cannot update business service', { cause: error });
  }
}

export async function getDefaultServiceIntakeQuestions(
  businessId: string,
): Promise<ServiceIntakeQuestion[]> {
  try {
    const response = await axios.get(
      `${API_URL}/v1/businesses/${businessId}/services/default-intake-questions`,
    );
    return response?.data?.result as ServiceIntakeQuestion[];
  } catch (error) {
    throw new Error('Cannot get default service intake questions', {
      cause: error,
    });
  }
}

export async function getServiceIntakeQuestions(
  businessId: string,
  serviceId: number,
): Promise<ServiceIntakeQuestion[]> {
  try {
    const response = await axios.get(
      `${API_URL}/v1/businesses/${businessId}/services/${serviceId}/intake-questions`,
    );
    return response?.data?.result as ServiceIntakeQuestion[];
  } catch (error) {
    throw new Error('Cannot get service intake questions', { cause: error });
  }
}

export async function updateServiceIntakeQuestions(
  businessId: string,
  serviceId: number,
  questions: { questionId: string; enabled: boolean }[],
): Promise<ServiceIntakeQuestion[]> {
  try {
    const response = await axios.patch(
      `${API_URL}/v1/businesses/${businessId}/services/${serviceId}/intake-questions`,
      { questions },
    );
    return response?.data?.result as ServiceIntakeQuestion[];
  } catch (error) {
    throw new Error('Cannot update service intake questions', { cause: error });
  }
}

export async function archiveBusinessService(
  businessId: string,
  serviceId: number,
) {
  try {
    const response = await axios.delete(
      `${API_URL}/v1/businesses/${businessId}/services/${serviceId}`,
    );

    return response?.data?.result as Services;
  } catch (error) {
    throw new Error('Cannot archive business service', { cause: error });
  }
}

export async function invalidateBusinessServices() {
  await client.invalidateQueries({
    queryKey: ['businessServices'],
    exact: false,
  });
}
export async function getBusinessStripeAccount(businessId: string) {
  try {
    const response = await axios.get(
      `${API_URL}/v1/businesses/${businessId}/stripe-account`,
    );

    return response?.data?.result as GetStripeAccountResponse;
  } catch (error) {
    throw new Error('Cannot get business stripe account', { cause: error });
  }
}

export async function linkBusinessStripeAccount(
  businessId: string,
  payload: LinkStripeAccountDto,
) {
  const response = await axios.post(
    `${API_URL}/v1/businesses/${businessId}/stripe-account/link`,
    payload,
  );

  return response?.data?.result as LinkStripeAccountResponse;
}

export async function loginBusinessStripeAccount(businessId: string) {
  try {
    const response = await axios.post(
      `${API_URL}/v1/businesses/${businessId}/stripe-account/login`,
    );

    return response?.data?.result as LinkStripeAccountResponse;
  } catch (error) {
    throw new Error('Cannot get stripe login link', { cause: error });
  }
}

export async function getBusinessOnboarding(businessId: string) {
  try {
    const response = await axios.get(
      `${API_URL}/v1/businesses/${businessId}/onboarding`,
    );

    return response?.data?.result as OnboardingResponse;
  } catch (error) {
    throw new Error('Cannot get business onboarding', { cause: error });
  }
}

export async function skipOnboardingStep(
  businessId: string,
  stepId: ShopOnboardingID,
) {
  try {
    const response = await axios.patch(
      `${API_URL}/v1/businesses/${businessId}/onboarding/${stepId}/skip`,
    );

    return response?.data?.result as SkipOnboardingStepResponse;
  } catch (error) {
    throw new Error('Cannot skip onboarding step', { cause: error });
  }
}

export async function completeOnboardingStep(
  businessId: string,
  stepId: ShopOnboardingID,
) {
  try {
    const response = await axios.patch(
      `${API_URL}/v1/businesses/${businessId}/onboarding/${stepId}/complete`,
    );

    return response?.data?.result as CompleteOnboardingStepResponse;
  } catch (error) {
    throw new Error('Cannot complete onboarding step', { cause: error });
  }
}

export async function getOnboardingSpecialistUrl({
  firstName,
  lastName,
  email,
  company,
}: {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
}) {
  try {
    const url =
      'https://getporter.chilipiper.com/concierge-router/onboarding_router/rest';
    const body = {
      form: {
        PersonFirstName: firstName,
        PersonLastName: lastName,
        CompanyName: company,
        PersonEmail: email,
      },
      options: {
        router: 'onboarding_router',
      },
    };

    const response = await baseAxios.post(url, body);

    return response.data as ChiliPiperResponse;
  } catch {
    // Empty
    return null;
  }
}

export async function createShopTimeBlocks(
  businessId: string,
  payload: ResourceBlock & {
    employeeIds: string[];
  },
) {
  try {
    const response = await axios.post(
      `${API_URL}/v1/businesses/${businessId}/resource-blocks`,
      payload,
    );
    return response.data.result as string[];
  } catch (error) {
    throw new Error('Cannot create time block', { cause: error });
  }
}

export async function updatePaymentsRates(
  businessId: string,
  payload: UpdatePaymentsRatesPayload,
) {
  const response = await axios.patch(
    `${API_URL}/v1/businesses/${businessId}/payment-rates`,
    payload,
  );

  return response?.data?.result as Pick<
    Businesses,
    | 'paymentsRate'
    | 'onlineCustomerServiceFeeRate'
    | 'isBnplEnabled'
    | 'bnplFeePayer'
  >;
}

export async function getBusinessPaymentsCapabilities(businessId: string) {
  const response = await axios.get(
    `${API_URL}/v1/businesses/${businessId}/capabilities`,
  );

  return response?.data?.result as PaymentsCapabilities;
}

export async function getBusinessStripeSummary(businessId: string) {
  const response = await axios.get(
    `${API_URL}/v1/businesses/${businessId}/stripe-account/summary`,
  );

  return response?.data?.result as StripeSummary;
}

export async function createBusinessStripeAccountSession(businessId: string) {
  const response = await axios.get(
    `${API_URL}/v1/businesses/${businessId}/stripe-account/session`,
  );

  return response?.data?.result as Stripe.AccountSession;
}

export async function initiateInstantPayout(
  businessId: string,
  amount: number,
) {
  const response = await axios.post(
    `${API_URL}/v1/businesses/${businessId}/stripe-account/payouts`,
    { amount },
  );

  return response?.data?.result as InitiatePayoutResponse;
}

export async function invalidateBusinessStripeSummary() {
  await client.invalidateQueries({
    queryKey: ['stripe-summary'],
    exact: false,
  });
}

export type BusinessWebRedesignFlag = {
  businessWebRedesignEligible: boolean;
  businessWebRedesignEnabled: boolean;
  businessWebRedesignLocked: boolean;
  businessWebRedesignActive: boolean;
};

export async function updateBusinessWebRedesign(
  businessId: string,
  enabled: boolean,
  reason?: string,
) {
  const response = await axios.patch(
    `${API_URL}/v1/businesses/${businessId}/business-web-redesign`,
    { enabled, ...(reason ? { reason } : {}) },
  );

  return response?.data?.result as BusinessWebRedesignFlag;
}
